import { useState } from "react";
import { CheckCircle2, Circle } from "lucide-react";

export const SecureCodingChecklist = () => {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const categories = [
    {
      title: "Authentication",
      items: [
        "Multi-factor authentication implemented",
        "Passwords hashed with bcrypt/Argon2 (salt rounds ≥12)",
        "Session tokens are random and unpredictable",
        "Session timeout implemented",
        "Failed login rate limiting in place",
        "Password reset tokens expire quickly",
      ],
    },
    {
      title: "Input Handling",
      items: [
        "All input validated server-side",
        "Schema validation with Zod/Yup/Joi",
        "SQL queries use parameterized statements",
        "File uploads validated (type, size, content)",
        "URL redirects validated against allowlist",
      ],
    },
    {
      title: "Output & Data",
      items: [
        "HTML output properly encoded",
        "JSON responses have correct Content-Type",
        "Sensitive data encrypted at rest",
        "TLS 1.2+ enforced for transit",
        "No sensitive data in URLs or logs",
      ],
    },
    {
      title: "Headers & Config",
      items: [
        "HTTPS enforced (HSTS enabled)",
        "CSP headers configured",
        "X-Frame-Options set",
        "X-Content-Type-Options: nosniff",
        "CORS properly restricted",
        "Cookies have Secure and HttpOnly flags",
      ],
    },
    {
      title: "Dependencies",
      items: [
        "npm audit / yarn audit runs regularly",
        "Automated dependency updates (Dependabot)",
        "Lock files committed to repo",
        "No known vulnerable dependencies",
      ],
    },
  ];

  const toggleItem = (item: string) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(item)) {
      newChecked.delete(item);
    } else {
      newChecked.add(item);
    }
    setCheckedItems(newChecked);
  };

  const totalItems = categories.reduce((acc, cat) => acc + cat.items.length, 0);
  const checkedCount = checkedItems.size;
  const progress = Math.round((checkedCount / totalItems) * 100);

  return (
    <section className="py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground">
            Security Checklist
          </h2>
          <p className="text-muted-foreground mt-1">
            Use this checklist before deploying any application.
          </p>
        </div>
        <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-card border border-border">
          <div className="text-2xl font-bold text-primary">{progress}%</div>
          <div className="text-sm text-muted-foreground">
            {checkedCount}/{totalItems} items
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category, catIndex) => (
          <div key={catIndex} className="bg-card rounded-xl border border-border p-5">
            <h3 className="font-semibold text-foreground mb-4">{category.title}</h3>
            <div className="space-y-3">
              {category.items.map((item, itemIndex) => {
                const isChecked = checkedItems.has(item);
                return (
                  <button
                    key={itemIndex}
                    onClick={() => toggleItem(item)}
                    className="flex items-start gap-3 text-left w-full group"
                  >
                    {isChecked ? (
                      <CheckCircle2 className="w-5 h-5 text-chart-4 shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5 group-hover:text-primary transition-colors" />
                    )}
                    <span className={`text-sm ${isChecked ? "text-muted-foreground line-through" : "text-foreground"}`}>
                      {item}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
